/// <reference types="@types/google.maps" />
import {
  AccuracyStatus,
  ConnectedDirectSyncAccount,
  EcosystemSource,
  LocalSearchData,
  Provider,
  SEODataSummary,
  SourceAccuracy,
  SyncData as SyncDataProto,
  SyncRequirement,
  SyncStatus,
  SyndicationStatus,
  Vicinity,
} from '@vendasta/listing-products';
import { formatNumber } from '@angular/common';
import { DateRange } from '@vendasta/galaxy/utility/date-utils';
import { ScoreData } from '@vendasta/listing-sync-pro';
import { TranslateService } from '@ngx-translate/core';
import { CategorySelectService } from '@galaxy/business-category';
import { LanguageService } from './listings-management/language-service.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CategoryRequest, CategoryType } from '@vendasta/category';
import { Pipe, PipeTransform } from '@angular/core';

export const SEARCH_RADIUS_LOCAL = 1.25;
export const SEARCH_RADIUS_SMALL_CITY = 2.5;
export const SEARCH_RADIUS_BIG_CITY = 5;
export const SEARCH_RADIUS_XL_CITY = 10;
export const SEARCH_RADIUS_METRO_AREA = 30;
export const SEARCH_RADIUS_REGION = 50;

export const AGIDTOKEN = 'AGID';
export const IS_IMPERSONATING_TOKEN = 'IS_IMPERSONATING';
export const DEMO_PAID_EDITION = 'EDITION-MXWLTQPN';
export const PROD_PAID_EDITION = 'EDITION-CFH5CKHC';
export const APP_ID = 'MS';

export const AppleSourceID = 13840;
export const FacebookSourceID = 10050;
export const GoogleSourceID = 10010;
export const InstagramSourceID = 11610;
export const XSourceID = 10060;
export const BingSourceID = 10020;
export const Top4SourceID = 14920;

export const LOCAL_SEO_UI_TRIGGER = 'local seo ui';

export const SEODataSearchRadiusZoomValue = {
  [SEARCH_RADIUS_LOCAL]: 12.6,
  [SEARCH_RADIUS_SMALL_CITY]: 11.55,
  [SEARCH_RADIUS_BIG_CITY]: 10.6,
  [SEARCH_RADIUS_XL_CITY]: 9.7,
  [SEARCH_RADIUS_METRO_AREA]: 8.4,
  [SEARCH_RADIUS_REGION]: 7.4,
};
export const SEODataSearchRadiusTypes = [
  SEARCH_RADIUS_LOCAL,
  SEARCH_RADIUS_SMALL_CITY,
  SEARCH_RADIUS_BIG_CITY,
  SEARCH_RADIUS_XL_CITY,
  SEARCH_RADIUS_METRO_AREA,
  SEARCH_RADIUS_REGION,
] as const;

export type SeoDataSearchRadiusType = (typeof SEODataSearchRadiusTypes)[number];

export const MaxRank = 50;
export const MaxMapsRank = 20;

export interface SEODataPoint {
  date: Date;
  value: number;
}

export interface SeoDataKeywordMetrics {
  dateRange?: DateRange;
  keyword?: string;
  localRank?: SEODataPoint[];
  organicRank?: SEODataPoint[];
}

export interface SeoDataSettings {
  businessId?: string;
  localSearchRadius?: number;
  favoriteKeywords?: string[];
}

export interface Stat {
  value: string;
  rawValue: number;
}

export class SEOTableData {
  keyword: Stat;
  date: Date;
  localRank: Stat;
  organicRank: Stat;
  localStartedAt: Stat;
  organicStartedAt: Stat;
  difficulty: Stat;
  searchVolume: Stat;
  metrics?: SeoDataKeywordMetrics;
  searchRadius?: number;
  isFavorite?: boolean;
  inProgress?: boolean;

  constructor(keyword: string, data: SEODataSummary, metrics: SeoDataKeywordMetrics, favoriteKeywords?: string[]) {
    if (!data) {
      this.inProgress = true;
    }

    this.keyword = { value: keyword, rawValue: 0 };
    this.date = data?.date;
    this.localRank = {
      value: convertRank(0, this.inProgress),
      rawValue: 0,
    };
    this.organicRank = {
      value: convertRank(0, this.inProgress),
      rawValue: 0,
    };
    if (metrics?.localRank.length > 0) {
      this.localRank = {
        value: convertRank(metrics.localRank[metrics.localRank.length - 1].value, this.inProgress),
        rawValue: metrics.localRank[metrics.localRank.length - 1].value,
      };
    }
    if (metrics?.organicRank.length > 0) {
      this.organicRank = {
        value: convertRank(metrics.organicRank[metrics.organicRank.length - 1].value, this.inProgress),
        rawValue: metrics.organicRank[metrics.organicRank.length - 1].value,
      };
    }
    this.difficulty = {
      value: convertDifficulty(data?.difficulty),
      rawValue: data?.difficulty,
    };
    this.searchVolume = {
      value: convertNumber(data?.searchVolume),
      rawValue: data?.searchVolume,
    };
    this.searchRadius = data?.searchRadius;
    this.metrics = metrics;

    this.localStartedAt = {
      value: convertRank(0, this.inProgress),
      rawValue: this.localRank.value === '—' || this.localRank.value === 'NR' ? 0 : MaxMapsRank,
    };
    this.organicStartedAt = {
      value: convertRank(0, this.inProgress),
      rawValue: this.organicRank.value === '—' || this.organicRank.value === 'NR' ? 0 : MaxRank,
    };
    if (metrics?.localRank.length > 0) {
      this.localStartedAt = {
        value: convertRank(metrics.localRank[0].value, this.inProgress),
        rawValue:
          this.localRank.value === '—' || this.localRank.value === 'NR' ? 0 : metrics.localRank[0].value || MaxMapsRank,
      };
    }
    if (metrics?.organicRank.length > 0) {
      this.organicStartedAt = {
        value: convertRank(metrics.organicRank[0].value, this.inProgress),
        rawValue:
          this.organicRank.value === '—' || this.organicRank.value === 'NR'
            ? 0
            : metrics.organicRank[0].value || MaxRank,
      };
    }
    this.isFavorite = favoriteKeywords?.includes(keyword);
  }
}

function convertDifficulty(value?: number): string {
  if (value === null || value === undefined || value === 0) {
    return 'N/A';
  }
  if (value < 33) {
    return 'Low';
  }
  if (value < 66) {
    return 'Medium';
  }
  return 'High';
}

function convertRank(value: number, inProgress: boolean): string {
  if (inProgress) {
    return '—';
  }
  if (value === null || value === undefined || value === 0) {
    return 'NR';
  }
  if (value > MaxRank) {
    return '50+';
  }
  return formatNumber(value, 'en-US', '1.0-2');
}

function convertNumber(value?: number): string {
  if (value === null || value === undefined || value === 0) {
    return 'N/A';
  }
  return formatNumber(value, 'en-US', '1.0-2');
}

export const LocaleDateTickFormatting = (val: any) => {
  if (val instanceof Date) {
    let locale = 'en';
    if (localStorage) {
      locale = localStorage['locale'] || 'en';
    }
    return (<Date>val).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  }
  return val;
};

export const WholeNumberTickFormatting = (val: any) => {
  if (val % 1 === 0) {
    return val.toLocaleString();
  }
  return '';
};

export interface SyncingProgressSummaryData {
  totalSourcesCount: number;
  syncedSourcesCount: number;
  syncInProgressSourcesCount: number;
  notSyncingSourcesCount: number;
}

export interface ListingsScoreSummaryData {
  listingScore: number;
  originalListingScore: number;
  industryAverageListingsScore: number;
  ninetyFifthPercentile: number;
  totalAcceptedSources: number;
  totalSources: number;
  totalAccurate: number;
  totalPossibleErrors: number;
  totalNotFound: number;
  history: { name: Date; value: number }[];
}

export function determineOriginalListingScore(scoreData: ScoreData): number {
  if (!scoreData?.listingScoreHistory || scoreData?.listingScoreHistory.length <= 0) {
    return 0;
  }

  return scoreData.listingScoreHistory[0].score || 0;
}

export interface SyncingProgressSyncLegend {
  icon: MaterialIcons;
  name: string;
  description: string;
  style: SyncStyle;
}

export enum MaterialIcons {
  DONE = 'done',
  SYNC = 'sync',
  SYNC_DISABLED = 'sync_disabled',
  SYNC_PROBLEM = 'sync_problem',
  VISIBILITY = 'visibility',
  WARNING = 'warning',
  LOCK = 'lock',
  LINK_OFF = 'link_off',
  NONE = '',
}

export enum SyncStyle {
  UNSET = 'unset',
  SUCCESS = 'success',
  WARNING = 'warning',
  NEUTRAL = 'neutral',
}

export interface DirectSubmitSourceConfig {
  RequiresAccount: boolean;
}

export const DIRECT_SUBMIT_SOURCE_CONFIG: Record<number, DirectSubmitSourceConfig> = {
  [GoogleSourceID]: {
    RequiresAccount: true,
  },
  [FacebookSourceID]: {
    RequiresAccount: true,
  },
  [XSourceID]: {
    RequiresAccount: true,
  },
  [InstagramSourceID]: {
    RequiresAccount: true,
  },
};

export interface AccuracyAndSyncMapping {
  syncIcon: MaterialIcons;
  style: SyncStyle;
  syncText: string;
  tooltipTitle?: string;
  tooltipText?: string;
}

export const SyncStatusMapping = new Map<SyncStatus, AccuracyAndSyncMapping>([
  [
    SyncStatus.SYNC_STATUS_SUCCESS,
    {
      syncIcon: MaterialIcons.DONE,
      style: SyncStyle.SUCCESS,
      syncText: 'LISTING_SYNC.SYNC_STATUS_SUCCESS.NAME',
    },
  ],
  [
    SyncStatus.SYNC_STATUS_SUBMITTED,
    {
      syncIcon: MaterialIcons.DONE,
      style: SyncStyle.SUCCESS,
      syncText: 'LISTING_SYNC.SYNC_STATUS_SUBMITTED.NAME',
    },
  ],
  [
    SyncStatus.SYNC_STATUS_SYNCING,
    {
      syncIcon: MaterialIcons.SYNC,
      style: SyncStyle.SUCCESS,
      syncText: 'LISTING_SYNC.SYNC_STATUS_SYNCING.NAME',
    },
  ],
  [
    SyncStatus.SYNC_STATUS_PARTIALLY_SYNCED,
    {
      syncIcon: MaterialIcons.SYNC_PROBLEM,
      style: SyncStyle.WARNING,
      syncText: 'LISTING_SYNC.SYNC_STATUS_PARTIALLY_SYNCED.NAME',
    },
  ],
  [
    SyncStatus.SYNC_STATUS_FAILED,
    {
      syncIcon: MaterialIcons.WARNING,
      style: SyncStyle.WARNING,
      syncText: 'LISTING_SYNC.SYNC_STATUS_FAILED.NAME',
    },
  ],
  [
    SyncStatus.SYNC_STATUS_CLAIMED,
    {
      syncIcon: MaterialIcons.LOCK,
      style: SyncStyle.WARNING,
      syncText: 'LISTING_SYNC.SYNC_STATUS_CLAIMED.NAME',
      tooltipTitle: 'LISTING_SYNC.LSP_ADDON.COLUMNS.SYNC_STATUS.NOT_CLAIMABLE.TOOLTIP.TITLE',
      tooltipText: 'LISTING_SYNC.LSP_ADDON.COLUMNS.SYNC_STATUS.NOT_CLAIMABLE.TOOLTIP.DESCRIPTION',
    },
  ],
  [
    SyncStatus.SYNC_STATUS_UNAVAILABLE,
    {
      syncIcon: MaterialIcons.SYNC_DISABLED,
      style: SyncStyle.NEUTRAL,
      syncText: 'LISTING_SYNC.SYNC_STATUS_UNAVAILABLE.NAME',
    },
  ],
  [
    SyncStatus.SYNC_STATUS_UNSET,
    {
      syncIcon: null,
      style: SyncStyle.NEUTRAL,
      syncText: 'LISTING_SYNC.SYNC_STATUS_UNSET.NAME',
    },
  ],
]);

export const SyncingProgressSyncLegendConfig: SyncingProgressSyncLegend[] = [
  {
    icon: MaterialIcons.DONE,
    name: 'LISTING_SYNC.SYNC_STATUS_SUCCESS.NAME',
    description: 'LISTING_SYNC.SYNC_STATUS_SUCCESS.DESCRIPTION',
    style: SyncStyle.SUCCESS,
  },
  {
    icon: MaterialIcons.DONE,
    name: 'LISTING_SYNC.SYNC_STATUS_SUBMITTED.NAME',
    description: 'LISTING_SYNC.SYNC_STATUS_SUBMITTED.DESCRIPTION',
    style: SyncStyle.SUCCESS,
  },
  {
    icon: MaterialIcons.SYNC,
    name: 'LISTING_SYNC.SYNC_STATUS_SYNCING.NAME',
    description: 'LISTING_SYNC.SYNC_STATUS_SYNCING.DESCRIPTION',
    style: SyncStyle.SUCCESS,
  },
  {
    icon: MaterialIcons.SYNC_PROBLEM,
    name: 'LISTING_SYNC.SYNC_STATUS_PARTIALLY_SYNCED.NAME',
    description: 'LISTING_SYNC.SYNC_STATUS_PARTIALLY_SYNCED.DESCRIPTION',
    style: SyncStyle.WARNING,
  },
  {
    icon: MaterialIcons.WARNING,
    name: 'LISTING_SYNC.SYNC_STATUS_FAILED.NAME',
    description: 'LISTING_SYNC.SYNC_STATUS_FAILED.DESCRIPTION',
    style: SyncStyle.WARNING,
  },
  {
    icon: MaterialIcons.LOCK,
    name: 'LISTING_SYNC.SYNC_STATUS_CLAIMED.NAME',
    description: 'LISTING_SYNC.SYNC_STATUS_CLAIMED.DESCRIPTION',
    style: SyncStyle.WARNING,
  },
];

export enum DirectSyncErrorCode {
  SUBMISSION_ERROR = 1,
  INVALID_CATEGORY_ERROR = 2,
  INVALID_FIELD_ERROR = 3,
  MISSING_FIELD_ERROR = 4,
  MISMATCHED_FIELD_ERROR = 5,
  AUTHENTICATION_ERROR = 6,
  AUTHORIZATION_ERROR = 7,
  UNABLE_TO_VALIDATE_ERROR = 8,
  CANNOT_HIDE_ADDRESS_ERROR = 9,
  UNABLE_TO_GEOCODE_ERROR = 10,
  UNABLE_TO_UPDATE_PHONE_ERROR = 11,
  IMAGE_REQUIREMENTS_ERROR = 12,
  MISSING_ADDRESS_ERROR = 13,
  CLAIMED_ERROR = 14,
  CANNOT_BE_CLOSED_ERROR = 15,
  URL_IN_DESCRIPTION_ERROR = 16,
}

export const DirectSyncErrorTranslationKeys: Record<DirectSyncErrorCode, string> = {
  [DirectSyncErrorCode.SUBMISSION_ERROR]: 'LISTING_SYNC.SYNC_STATUS.UNKNOWN_ERROR',
  [DirectSyncErrorCode.INVALID_CATEGORY_ERROR]: 'LISTING_SYNC.SYNC_STATUS.INVALID_CATEGORY',
  [DirectSyncErrorCode.INVALID_FIELD_ERROR]: 'LISTING_SYNC.SYNC_STATUS.INVALID_FIELD',
  [DirectSyncErrorCode.MISSING_FIELD_ERROR]: 'LISTING_SYNC.SYNC_STATUS.MISSING_FIELD',
  [DirectSyncErrorCode.MISMATCHED_FIELD_ERROR]: 'LISTING_SYNC.SYNC_STATUS.MISMATCHED_FIELD',
  [DirectSyncErrorCode.AUTHENTICATION_ERROR]: 'LISTING_SYNC.SYNC_STATUS.AUTHENTICATION_ERROR',
  [DirectSyncErrorCode.AUTHORIZATION_ERROR]: 'LISTING_SYNC.SYNC_STATUS.AUTHORIZATION_ERROR',
  [DirectSyncErrorCode.UNABLE_TO_VALIDATE_ERROR]: 'LISTING_SYNC.SYNC_STATUS.UNABLE_TO_VALIDATE',
  [DirectSyncErrorCode.CANNOT_HIDE_ADDRESS_ERROR]: 'LISTING_SYNC.SYNC_STATUS.CANNOT_HIDE_ADDRESS',
  [DirectSyncErrorCode.UNABLE_TO_GEOCODE_ERROR]: 'LISTING_SYNC.SYNC_STATUS.UNABLE_TO_GEOCODE',
  [DirectSyncErrorCode.UNABLE_TO_UPDATE_PHONE_ERROR]: 'LISTING_SYNC.SYNC_STATUS.UNABLE_TO_UPDATE_PHONE',
  [DirectSyncErrorCode.IMAGE_REQUIREMENTS_ERROR]: 'LISTING_SYNC.SYNC_STATUS.IMAGE_REQUIREMENTS',
  [DirectSyncErrorCode.MISSING_ADDRESS_ERROR]: 'LISTING_SYNC.SYNC_STATUS.MISSING_ADDRESS',
  [DirectSyncErrorCode.CLAIMED_ERROR]: 'LISTING_SYNC.SYNC_STATUS_CLAIMED.DESCRIPTION',
  [DirectSyncErrorCode.CANNOT_BE_CLOSED_ERROR]: 'LISTING_SYNC.SYNC_STATUS.CANNOT_BE_CLOSED',
  [DirectSyncErrorCode.URL_IN_DESCRIPTION_ERROR]: 'LISTING_SYNC.SYNC_STATUS.URL_IN_DESCRIPTION',
};

export interface MismatchedField {
  field: string;
  current: string;
  suggested: string;
}

export interface SyncError {
  message: string;
  listedFields?: string[];
}

export class DirectSyncErrorMessage {
  mismatchedFieldDetails?: MismatchedField[];
  missingFields?: string[];
  invalidFields?: string[];
  errorCode?: DirectSyncErrorCode;
  sourceName?: string;
  currentCategory?: string;
  suggestedCategory?: string;
  link?: string;
  rawMessage?: string;

  private translate: TranslateService;
  private categoryService: CategorySelectService;
  private languageService: LanguageService;

  constructor(
    translate: TranslateService,
    categoryService: CategorySelectService,
    languageService: LanguageService,
    rawJSON: string,
  ) {
    this.translate = translate;
    this.categoryService = categoryService;
    this.languageService = languageService;

    const parsed = JSON.parse(rawJSON);
    this.mismatchedFieldDetails = parsed?.mismatchedFieldDetails;
    this.missingFields = parsed?.missingFields;
    this.invalidFields = parsed?.invalidFields;
    this.errorCode = parsed?.errorCode;
    this.sourceName = parsed?.sourceName;
    this.currentCategory = parsed?.currentCategory;
    this.suggestedCategory = parsed?.suggestedCategory;
    this.link = parsed?.link;
    this.rawMessage = parsed?.rawMessage;
  }

  get fields(): string[] {
    let fields: string[] = [];
    switch (this.errorCode) {
      case DirectSyncErrorCode.MISSING_FIELD_ERROR:
        fields = this.missingFields;
        break;
      case DirectSyncErrorCode.IMAGE_REQUIREMENTS_ERROR:
      case DirectSyncErrorCode.INVALID_FIELD_ERROR:
        fields = this.invalidFields;
        break;
      case DirectSyncErrorCode.MISMATCHED_FIELD_ERROR:
        return this.formatMismatchedFields;
    }
    return fields.map((field) => getTranslatedErrorMessageField(this.translate, field));
  }

  get formatMismatchedFields(): string[] {
    const current = this.translate.instant('LISTING_SYNC.SYNC_STATUS.CURRENT');
    const suggested = this.translate.instant('LISTING_SYNC.SYNC_STATUS.SUGGESTED');
    return this.mismatchedFieldDetails.map((field) => {
      const fieldName = getTranslatedErrorMessageField(this.translate, field.field);
      return `\n${fieldName}: \n${current}: ${field.current}, \n${suggested}: ${field.suggested},\n`;
    });
  }

  get translatedError$(): Observable<SyncError> {
    if (!this.errorCode) {
      return of({
        message: this.rawMessage,
      });
    }

    const key = DirectSyncErrorTranslationKeys[this.errorCode];
    if (this.errorCode === DirectSyncErrorCode.INVALID_CATEGORY_ERROR) {
      return this.getCategories(this.currentCategory, this.suggestedCategory).pipe(
        map((cats) => {
          return {
            message: this.translate.instant(key, {
              source: this.sourceName,
              current_category: cats.at(0),
              suggested_category: cats.at(1),
            }),
          };
        }),
      );
    }
    if (this.errorCode === DirectSyncErrorCode.IMAGE_REQUIREMENTS_ERROR) {
      const field = this.invalidFields.length > 0 ? this.invalidFields[0] : '';
      const requirements = getImageRequirementByField(field);
      let translatedField = this.fields?.join('');
      translatedField = this.link ? `<a target="_blank" href="${this.link}">${translatedField}</a>` : translatedField;

      return of({
        message: this.translate.instant(key, {
          source: this.sourceName,
          field: translatedField,
          requirements: this.translate.instant(requirements),
        }),
      });
    }

    return of({
      message: this.translate.instant(key, {
        source: this.sourceName,
        field: this.fields?.join(''),
        link: this.link,
      }),
    });
  }

  private getCategories(currentCat: string, suggestedCat: string): Observable<string[]> {
    const currentAndSuggested: CategoryRequest[] = [
      new CategoryRequest({
        categoryType: CategoryType.CATEGORY_TYPE_V_CATEGORY,
        externalId: currentCat,
      }),
      new CategoryRequest({
        categoryType: CategoryType.CATEGORY_TYPE_V_CATEGORY,
        externalId: suggestedCat,
      }),
    ];

    this.categoryService.language$$.next(this.languageService.getCurrentLang());

    return this.categoryService
      .getCategoryByExternalIDsAndType(currentAndSuggested)
      .pipe(map((categories) => categories.map((cat) => cat.name)));
  }
}

export function syncBadgeIcon(source: SyncDataProto): MaterialIcons {
  if (source.account?.tokenBroken) {
    if (source.account.tokenBroken) {
      return MaterialIcons.LINK_OFF;
    }
  }

  if (source.account?.connectionId && !source.account?.isAuthenticated) {
    return MaterialIcons.WARNING;
  }

  if (source.isSyncingEnabled) {
    return SyncStatusMapping.get(source.syncStatus)?.syncIcon;
  }
  return MaterialIcons.NONE;
}

export function accuracyBadgeStyle(src: SyncDataProto): SyncStyle {
  if (src.accuracy.status === AccuracyStatus.ACCURACY_STATUS_ACCURATE) {
    return SyncStyle.SUCCESS;
  }
  return SyncStyle.UNSET;
}

export function syncBadgeStyle(source: SyncDataProto): SyncStyle {
  if (source.account?.tokenBroken) {
    return SyncStyle.UNSET;
  }
  if (source.account?.connectionId && !source.account?.isAuthenticated) {
    return SyncStyle.WARNING;
  }

  if (source.isSyncingEnabled) {
    return SyncStatusMapping.get(source.syncStatus)?.style;
  }
  return SyncStyle.NEUTRAL;
}

export function getSyncStatusText(
  translate: TranslateService,
  sourceId: number,
  sourceName: string,
  listingFound: boolean,
  accuracy: AccuracyStatus,
  hasAccountConnected: boolean,
  isSyncingEnabled: boolean,
  locked: boolean,
  isTokenBroken: boolean,
  isVerified: boolean,
  isGoogleTokenBroken: boolean,
  syncStatus: SyncStatus,
): string {
  if (sourceId == GoogleSourceID && isGoogleTokenBroken) {
    return translate.instant('LISTING_SYNC.SYNCING_STATUS.BROKEN');
  }

  if (sourceId == GoogleSourceID && hasAccountConnected && !isVerified) {
    return translate.instant('LISTING_SYNC.SYNCING_STATUS.PROFILE_UNVERIFIED');
  }

  if (listingIsBeingCreated(sourceId, isSyncingEnabled, accuracy, syncStatus)) {
    return translate.instant('LISTING_SYNC.SYNCING_STATUS.CREATING');
  }

  if (locked) {
    if (!listingFound) {
      return translate.instant('LISTING_SYNC.SYNCING_STATUS.LOCKED_UPGRADE_TO_CREATE');
    }
    return translate.instant('LISTING_SYNC.SYNCING_STATUS.LOCKED_UPGRADE_TO_SYNC');
  }

  if (!hasAccountConnected) {
    if (sourceId === InstagramSourceID) {
      return translate.instant('LISTING_SYNC.SYNCING_STATUS.CONNECT_FACEBOOK_TO_SYNC');
    }
    if (!listingFound) {
      return translate.instant('LISTING_SYNC.SYNCING_STATUS.CONNECT_OR_CREATE');
    }
    if (accuracy !== AccuracyStatus.ACCURACY_STATUS_ACCURATE) {
      return translate.instant('LISTING_SYNC.SYNCING_STATUS.SYNC_TO_FIX') + ' ' + sourceName;
    }
    return translate.instant('LISTING_SYNC.SYNCING_STATUS.SYNC_TO_KEEP_ACCURATE');
  }
  if (isTokenBroken) {
    return translate.instant('LISTING_SYNC.SYNCING_STATUS.BROKEN');
  }
  if (!isSyncingEnabled) {
    if (!listingFound) {
      return translate.instant('LISTING_SYNC.SYNCING_STATUS.SYNC_TO') + ' ' + sourceName;
    }
    if (accuracy !== AccuracyStatus.ACCURACY_STATUS_ACCURATE) {
      return translate.instant('LISTING_SYNC.SYNCING_STATUS.SYNC_TO_FIX') + ' ' + sourceName;
    }
    return translate.instant('LISTING_SYNC.SYNCING_STATUS.SYNC_TO_KEEP_ACCURATE');
  }

  if (syncStatus === SyncStatus.SYNC_STATUS_SUCCESS && accuracy == AccuracyStatus.ACCURACY_STATUS_UNAVAILABLE) {
    return translate.instant('LISTING_SYNC.SYNC_STATUS_SUBMITTED.NAME');
  }

  return translate.instant(SyncStatusMapping.get(syncStatus)?.syncText);
}

export function listingIsBeingCreated(
  sourceId: number,
  syncingEnabled: boolean,
  accuracyStatus: AccuracyStatus,
  syncStatus: SyncStatus,
): boolean {
  return (
    syncingEnabled &&
    (sourceId == BingSourceID || sourceId == AppleSourceID) &&
    accuracyStatus === AccuracyStatus.ACCURACY_STATUS_UNKNOWN &&
    syncStatus === SyncStatus.SYNC_STATUS_SUCCESS
  );
}

function getImageRequirementByField(field: string) {
  switch (field) {
    case 'Logo':
      return 'LISTING_SYNC.SYNC_STATUS.FIELDS.LOGO_REQUIREMENTS';
    case 'Cover Photo':
      return 'LISTING_SYNC.SYNC_STATUS.FIELDS.COVER_REQUIREMENTS';
    default:
      return 'LISTING_SYNC.SYNC_STATUS.FIELDS.PHOTO_REQUIREMENTS';
  }
}

export function getTranslatedErrorMessageField(translate: TranslateService, field: string): string {
  switch (field) {
    case 'Company Name':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.COMPANY_NAME');
    case 'Address':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.ADDRESS');
    case 'Phone Number':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.PHONE_NUMBER');
    case 'Zip/Postal Code':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.ZIP_CODE');
    case 'Website':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.WEBSITE');
    case 'City':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.CITY');
    case 'Category':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.CATEGORY');
    case 'State/Province':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.STATE');
    case 'Country/Region':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.COUNTRY');
    case 'Business':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.BUSINESS');
    case 'Geolocation':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.GEOLOCATION');
    case 'Logo':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.LOGO');
    case 'Cover Photo':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.COVER');
    case 'Hours':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.HOURS');
    case 'Description':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.DESCRIPTION');
    case 'Email':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.EMAIL');
    case 'Photos':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.PHOTOS');
    case 'Payment Methods':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.PAYMENT_METHODS');
    case 'Social URLs':
      return translate.instant('LISTING_SYNC.SYNC_STATUS.FIELDS.SOCIAL_URLS');
  }
}

@Pipe({
  name: 'ordinalNumber',
})
export class OrdinalNumberPipe implements PipeTransform {
  static nth(n: number): string {
    if ([11, 12, 13].includes(n % 100)) return 'th'; // Ensure correct suffix for 11th, 12th, 13th
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  transform(value: number): string {
    if (!value) {
      return '';
    }
    return `${value}${OrdinalNumberPipe.nth(value)}`;
  }
}

export interface BusinessPerformance {
  ranking: string;
  color: string;
}

export interface MapMarker {
  label: google.maps.MarkerLabel;
  lat: string;
  lng: string;
  icon: google.maps.Symbol;
}

export function buildMapMarkers(searches: LocalSearchData[]): MapMarker[] {
  const businessMarkers = [];
  const ordinalPipe = new OrdinalNumberPipe();

  searches.forEach((s) => {
    let opacity = 0.6;
    if (s.vicinity === Vicinity.VICINITY_CITY || s.vicinity === Vicinity.VICINITY_C3) {
      opacity = 1;
    }

    const markerBase = {
      path: 0,
      strokeWeight: 2,
      strokeColor: 'white',
      scale: 22,
      fillOpacity: opacity,
    };

    const mainBusinessFiltered = s.results?.filter((b) => b.isMainBusiness);
    const mainBusiness = mainBusinessFiltered.length > 0 ? mainBusinessFiltered[0] : null;
    if (mainBusiness) {
      const markerIcon = {
        ...markerBase,
        fillColor: businessPerformance(+mainBusiness.rank).color,
      };

      if (s.searchLocation) {
        businessMarkers.push({
          label: {
            text: mainBusiness?.rank ? ordinalPipe.transform(+mainBusiness.rank) : '-',
            color: 'white',
          },
          lat: s.searchLocation.latitude,
          lng: s.searchLocation.longitude,
          icon: markerIcon,
        });
      }
    } else {
      businessMarkers.push({
        label: {
          text: '-',
          color: 'white',
        },
        lat: s.searchLocation.latitude,
        lng: s.searchLocation.longitude,
        icon: {
          ...markerBase,
          fillColor: SEOPerformance.UNKNOWN.color,
        },
      });
    }
  });
  return businessMarkers;
}

export function businessPerformance(rank: number): BusinessPerformance {
  if (rank > 0 && rank <= 3) {
    return SEOPerformance.TOP;
  }
  if (rank > 3 && rank <= 20) {
    return SEOPerformance.AVERAGE;
  }
  if (rank > 20 && rank <= 100) {
    return SEOPerformance.BOTTOM;
  }
  return SEOPerformance.UNKNOWN;
}

interface Status {
  text: string;
  style?: SyncStyle;
  icon?: MaterialIcons;
  tooltip?: string;
  tooltipTitle?: string;
}

export interface SyncData {
  sourceId: number;
  name: string;
  accuracy: SourceAccuracy;
  score: number;
  maxScore: number;
  listingUrl: string;
  listingId: string;

  account?: ConnectedDirectSyncAccount;
  additionalAccounts?: ConnectedDirectSyncAccount[];
  connectLink?: string;
  clientTags?: string[];
  sourceHasReporting?: boolean;
  syncDetails?: SyndicationStatus;
  syncStatus?: SyncStatus;
  isSyncingEnabled?: boolean;
  ecosystemSources?: EcosystemSource[];
  isListingVerified?: boolean;
  hidden?: boolean;
  lastUpdatedDate?: Date;
  errors?: DirectSyncErrorMessage[];
  provider?: Provider;
  requirement: SyncRequirement;

  // computed
  sourceType?: string;
  errorMessages?: SyncError[];
  syncInfo?: Status;
  accuracyInfo?: Status;
  sourceIcon?: string;
  lastUpdated?: string;
  locked?: boolean;
}

export interface KeywordStore {
  keywords: string[];
  syncingKeywords: string[];
  favoriteKeywords: string[];
}

//
export const SEOPerformance = {
  TOP: { ranking: 'top', color: '#4CAF50' },
  AVERAGE: { ranking: 'average', color: '#FFB300' },
  BOTTOM: { ranking: 'bottom', color: '#C62828' },
  UNKNOWN: { ranking: 'unknown', color: '#C62828' },
};
